// Enhanced DropzoneArea Component with improved accessibility
import React, { useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { FiUpload } from 'react-icons/fi';
import StatusBar from './StatusBar';
import { ACCEPTED_FILE_TYPES, MAX_SIZE_BYTES } from '../utils/constants';

/**
 * Accessible drag and drop area for file uploads
 * - Enhanced keyboard navigation
 * - Improved focus management
 * - Better screen reader instructions
 *
 * @param {Object} props - Component props
 * @param {boolean} props.hasFiles - Whether any files have been added
 * @param {boolean} props.isUploading - Whether files are currently uploading
 * @param {boolean} props.isComplete - Whether upload is complete
 * @param {Function} props.onFilesDrop - Function called when files are dropped or selected
 * @param {Function} props.onFileDialogOpen - Optional function to call when file dialog opens
 * @param {Function} props.onDropRejected - Function called when files are rejected
 * @param {number} props.videoLimit - Maximum number of videos allowed
 * @param {number} props.maxSizeBytes - Maximum file size in bytes
 * @param {Object} props.mainButtonRef - Ref for the main button (for focus management)
 * @returns {JSX.Element} Accessible dropzone area component
 */
const DropzoneArea = ({
  hasFiles,
  isUploading,
  isComplete,
  onFilesDrop,
  onFileDialogOpen,
  onDropRejected,
  videoLimit = 3,
  maxSizeBytes = MAX_SIZE_BYTES,
  mainButtonRef,
}) => {
  // Ref for status updates to screen readers
  const dropzoneStatusRef = useRef(null);

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive, isDragReject, open } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxSize: maxSizeBytes,
    disabled: isUploading || isComplete,
    onDrop: (acceptedFiles, rejectedFiles) => {
      // Update screen reader status
      if (dropzoneStatusRef.current) {
        dropzoneStatusRef.current.textContent = `${acceptedFiles.length} files selected${
          rejectedFiles.length > 0 ? `, ${rejectedFiles.length} files rejected` : ''
        }`;
      }
      onFilesDrop(acceptedFiles);
      if (rejectedFiles.length > 0) {
        onDropRejected(rejectedFiles);
      }
    },
    onDragEnter: () => {
      // Announce drag enter to screen readers
      if (dropzoneStatusRef.current) {
        dropzoneStatusRef.current.textContent = 'Files detected. Drop to upload.';
      }
    },
    onDragLeave: () => {
      // Clear announcement when drag leaves
      if (dropzoneStatusRef.current) {
        dropzoneStatusRef.current.textContent = '';
      }
    },
  });

  // Handle select button click with focus management
  const handleSelectClick = e => {
    e.stopPropagation(); // Stop event propagation to prevent dropzone click
    if (onFileDialogOpen) {
      onFileDialogOpen();
    }
    open(e);
  };

  // Get appropriate aria attributes
  const getAriaAttributes = () => {
    const baseAttributes = {
      role: 'region',
      'aria-label': 'File upload dropzone',
      'aria-describedby': 'dropzone-instructions',
    };

    if (isUploading) {
      return {
        ...baseAttributes,
        'aria-busy': 'true',
      };
    }

    if (isComplete) {
      return {
        ...baseAttributes,
        'aria-disabled': 'true',
      };
    }

    if (isDragReject) {
      return {
        ...baseAttributes,
        'aria-invalid': 'true',
      };
    }

    return baseAttributes;
  };

  // Empty state (no files added yet)
  if (!hasFiles) {
    return (
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <div
            {...getRootProps()}
            {...getAriaAttributes()}
            className={`
              uploader-dropzone-empty
              border-2 rounded p-5
              ${isDragReject ? 'border-danger border-dashed bg-danger bg-opacity-10' : ''}
              ${isDragActive && !isDragReject ? 'border-success border-dashed bg-success bg-opacity-10' : ''}
              ${!isDragActive && !isDragReject ? 'border-secondary border-dashed bg-light' : ''}
            `}
            style={{
              cursor: isUploading || isComplete ? 'not-allowed' : 'pointer',
              opacity: isUploading || isComplete ? 0.6 : 1,
              transition: 'all 0.3s ease',
              minHeight: '280px',
            }}
            tabIndex={isUploading || isComplete ? -1 : 0}
            onKeyDown={e => {
              // Allow activation with Enter or Space key
              if (!isUploading && !isComplete && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                open(e);
              }
            }}>
            <input {...getInputProps()} aria-label="File upload input" />

            {/* Status updates for screen readers */}
            <div ref={dropzoneStatusRef} className="sr-only" aria-live="polite" role="status"></div>

            <div className="d-flex flex-column align-items-center justify-content-center h-100 gap-3 text-center">
              <div className="upload-icon-container" aria-hidden="true">
                <FiUpload
                  size={48}
                  className={
                    isDragReject
                      ? 'text-danger' + ' size-large-plus'
                      : 'text-primary' + ' icon-ff-button-primary size-large-plus'
                  }
                />
              </div>
              <div>
                <h2 className="fw-bold mb-2 h5 font-size-xlarge">
                  {isDragReject ? 'Image & Videos files only' : 'Drop Your Photos and Videos Here'}
                </h2>
                <p id="dropzone-instructions" className="mb-0 font-size-large">
                  Drag and drop your files here, or click to browse files. Accepts image and video
                  files only. Maximum {videoLimit} videos allowed.
                </p>
              </div>

              <button
                ref={mainButtonRef}
                onClick={handleSelectClick}
                className="btn ff-button--primary btn-lg d-flex align-items-center justify-content-center gap-2 px-4 py-2 mt-3 fs-6 d-flex align-items-center font-size-large"
                disabled={isUploading || isComplete}
                aria-disabled={isUploading || isComplete}
                aria-label="Select files for upload">
                <FiUpload
                  size={18}
                  aria-hidden="true"
                  className="icon-transparent size-medium-plus"
                />
                Select Files
              </button>

              {/* Size limit info */}
              <StatusBar
                variant="minimal"
                videoLimit={videoLimit}
                totalSize={maxSizeBytes}
                imageCount={0}
                videoCount={0}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Compact state (files already added)
  return (
    <div
      {...getRootProps()}
      {...getAriaAttributes()}
      className={`
        uploader-dropzone h-100
        border-2 rounded p-4 fs-6
        ${isDragReject ? 'border-danger border-dashed bg-danger bg-opacity-10' : ''}
        ${isDragActive && !isDragReject ? 'border-success border-dashed bg-success bg-opacity-10' : ''}
        ${!isDragActive && !isDragReject ? 'border-secondary border-dashed bg-light' : ''}
      `}
      style={{
        cursor: isUploading || isComplete ? 'not-allowed' : 'pointer',
        opacity: isUploading || isComplete ? 0.6 : 1,
        transition: 'all 0.3s ease',
        minHeight: '220px',
      }}
      tabIndex={isUploading || isComplete ? -1 : 0}
      onKeyDown={e => {
        // Allow activation with Enter or Space key
        if (!isUploading && !isComplete && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          open(e);
        }
      }}>
      <input {...getInputProps()} aria-label="File upload input" />

      {/* Status updates for screen readers */}
      <div ref={dropzoneStatusRef} className="sr-only" aria-live="polite" role="status"></div>

      <div className="d-flex flex-column align-items-center justify-content-center h-100 gap-3 text-center">
        <div className="upload-icon-container" aria-hidden="true">
          <FiUpload
            size={36}
            className={
              isDragReject
                ? 'text-danger' + ' size-large-plus'
                : 'text-primary' + ' icon-ff-button-primary size-large-plus'
            }
          />
        </div>
        <div>
          <h3 className="fw-bold mb-1 h6 font-size-medium">
            {isDragReject
              ? 'Image & Videos files only'
              : isComplete
                ? 'Upload complete'
                : isUploading
                  ? 'Uploading...'
                  : 'Drop More Files Here'}
          </h3>
          {!isComplete && !isUploading && (
            <>
              <p id="dropzone-instructions" className="small mb-2 font-size-medium">
                Drag and drop more files here, or click to browse
              </p>
              <button
                ref={mainButtonRef}
                onClick={handleSelectClick}
                className="btn btn-sm ff-button--primary d-inline-flex align-items-center gap-1 px-3 font-size-large"
                disabled={isUploading || isComplete}
                aria-disabled={isUploading || isComplete}
                aria-label="Add more files">
                <FiUpload size={14} aria-hidden="true" className="icon-transparent size-small" />
                Add Files
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DropzoneArea;
